import { useZustandHasDefaultReturnWindow } from "@faire/retailer-visitor-shared/components/ExtendReturnsModal/useHasDefaultReturnWindow";
import { RetailerProviders } from "@faire/retailer-visitor-shared/hooks/retailer/RetailerProviders";
import { useZustandRetailerFlashSale } from "@faire/retailer-visitor-shared/hooks/retailer/useFlashSale";
import { useZustandRetailerMultipleStores } from "@faire/retailer-visitor-shared/hooks/retailer/useHasEverHadMultipleStores";
import { useZustandRetailerInsider } from "@faire/retailer-visitor-shared/hooks/retailer/useInsider";
import { useZustandRetailerNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { useZustandRetailerPaymentTerm } from "@faire/retailer-visitor-shared/hooks/retailer/usePaymentTerm";
import { useZustandRetailerStatus } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailerStatus";
import { useZustandRetailerType } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailerType";
import { useZustandRetailerHighReturner } from "@faire/retailer-visitor-shared/hooks/retailer/useShouldShowHighReturnerOrderNudges";
import { useZustandRetailerSubUser } from "@faire/retailer-visitor-shared/hooks/retailer/useSubUser";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { useSettingMigrateRetailerStore } from "@faire/retailer-visitor-shared/settings/useSettingMigrateRetailerStore";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import { useZustandIsRetailerCreatedWithinLast30Days } from "@faire/retailer-visitor-shared/stores/ui/useIsRetailerCreatedWithinLast30Days";
import { useZustandExpandingOffer } from "@faire/retailer-visitor-shared/views/expanding-retailers/useExpandingOffer";
import { useZustandRetailerBusinessCategory } from "@faire/retailer-visitor-shared/views/underwriting-request/useRetailerBusinessCategory";
import fetchRetailer from "@faire/web-api--source/endpoints/www/api/retailer/retailerToken/get";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { createContext, useContext, useEffect, useState } from "react";
import { createStore, useStore } from "zustand";
import { StoreApi } from "zustand";

type RetailerCore = {
  retailer: IRetailer | undefined;
  refetchRetailer: () => Promise<void>;
  setRetailer: (retailer: IRetailer | undefined) => void;
  retailerName: string | undefined;
  retailerToken: string | undefined;
};

const RetailerCoreContext = createContext<StoreApi<RetailerCore> | undefined>(
  undefined
);

export const RetailerCoreProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const initialRetailer = getRetailer();
  const [store] = useState(() =>
    createStore<RetailerCore>((set) => ({
      retailer: initialRetailer,
      setRetailer: (newRetailer: IRetailer | undefined) => {
        if (!newRetailer) {
          return;
        }
        set({
          retailer: newRetailer,
          retailerName: newRetailer.name,
          retailerToken: newRetailer.token,
        });
      },
      refetchRetailer: async () => {
        const retailer = await fetchRetailer("me");
        set({
          retailer,
          retailerName: retailer.name,
          retailerToken: retailer.token,
        });
      },
      retailerName: initialRetailer?.name,
      retailerToken: initialRetailer?.token,
    }))
  );

  return (
    <RetailerCoreContext.Provider value={store}>
      <RetailerProviders>
        {children}
        <Synchronizer />
      </RetailerProviders>
    </RetailerCoreContext.Provider>
  );
};

const Synchronizer: React.FC = () => {
  const shouldUseHooksMigration = useSettingMigrateRetailerStore();
  const { userCanPlaceOrder } = useUserStore(["userCanPlaceOrder"]);
  const retailer = useRetailerCoreWithSelector((state) => state.retailer);
  const setRetailerType = useZustandRetailerType(
    (state) => state.setRetailerType
  );
  const setRetailerStatus = useZustandRetailerStatus(
    (state) => state.setRetailerStatus
  );
  const setRetailerNationality = useZustandRetailerNationality(
    (state) => state.setRetailerNationality
  );
  const setRetailerHighReturner = useZustandRetailerHighReturner(
    (state) => state.setRetailerHighReturner
  );
  const setRetailerSubUser = useZustandRetailerSubUser(
    (state) => state.setRetailerSubUser
  );
  const setRetailerPaymentTerm = useZustandRetailerPaymentTerm(
    (state) => state.setRetailerPaymentTerm
  );
  const setRetailerInsider = useZustandRetailerInsider(
    (state) => state.setRetailerInsider
  );
  const setRetailerMultipleStores = useZustandRetailerMultipleStores(
    (state) => state.setRetailerMultipleStores
  );
  const setRetailerFlashSale = useZustandRetailerFlashSale(
    (state) => state.setRetailerFlashSale
  );
  const setRetailerBusinessCategory = useZustandRetailerBusinessCategory(
    (state) => state.setRetailerBusinessCategory
  );
  const setHasDefaultReturnWindow = useZustandHasDefaultReturnWindow(
    (state) => state.setHasDefaultReturnWindow
  );
  const setExpandingOffer = useZustandExpandingOffer(
    (state) => state.setExpandingOffer
  );
  const setIsRetailerCreatedWithinLast30Days =
    useZustandIsRetailerCreatedWithinLast30Days(
      (state) => state.setIsRetailerCreatedWithinLast30Days
    );

  if (!shouldUseHooksMigration) {
    return null;
  }

  // eslint-disable-next-line react-compiler/react-compiler, react-hooks/rules-of-hooks
  useEffect(() => {
    if (retailer) {
      setRetailerType(retailer);
      setRetailerStatus(retailer);
      setRetailerNationality(retailer);
      setRetailerHighReturner(retailer);
      setRetailerSubUser(retailer);
      setRetailerPaymentTerm(retailer);
      setRetailerInsider(retailer);
      setRetailerMultipleStores(retailer);
      setRetailerFlashSale(retailer, userCanPlaceOrder);
      setRetailerBusinessCategory(retailer);
      setHasDefaultReturnWindow(retailer);
      setExpandingOffer(retailer);
      setIsRetailerCreatedWithinLast30Days(retailer);
    }
  }, [
    retailer,
    setRetailerType,
    setRetailerStatus,
    setRetailerNationality,
    setRetailerHighReturner,
    setRetailerSubUser,
    setRetailerPaymentTerm,
    setRetailerInsider,
    setRetailerMultipleStores,
    setRetailerFlashSale,
    setRetailerBusinessCategory,
    setHasDefaultReturnWindow,
    setExpandingOffer,
    userCanPlaceOrder,
    setIsRetailerCreatedWithinLast30Days,
  ]);
  return null;
};

export const useRetailerCore = () => {
  const context = useContext(RetailerCoreContext);
  if (!context) {
    throw new Error(
      "RetailerCoreContext not found. Must be used within RetailerCoreProvider"
    );
  }
  return useStore(context);
};

export const useRetailerCoreWithSelector = <T,>(
  selector: (state: RetailerCore) => T
): T => {
  const context = useContext(RetailerCoreContext);
  if (!context) {
    throw new Error(
      "RetailerCoreContext not found. Must be used within RetailerCoreProvider"
    );
  }
  return useStore(context, selector);
};
